import './cop-header-mobile.scss'

const MOBILE_BREAKPOINT = '(max-width: 1239px)'
const CUSTOMER_NAME_KEY = 'cop:customer-name'
const ACCOUNT_NAME_CLASS = 'cop-header-mobile__account-name'
const SWITCH_ACCOUNT_LABEL_CLASS = 'cop-header-mobile__switch-account-label'
const PROFILE_DRAWER_CLASS = 'cop-header-mobile__profile-drawer'
const ARIA_EXPANDED = 'aria-expanded'

function isPowerPortalMobile(): boolean {
  return document.body.classList.contains('cmp-powerportal-main') && window.matchMedia(MOBILE_BREAKPOINT).matches
}

// Mobile browser toolbars (address bar) shrink/grow the visible viewport without changing 100vh,
// so cop-header-mobile.scss's `calc(var(--vh, 1vh) * 100 - ...)` needs this kept in sync.
function setVhCustomProperty(): void {
  const height = window.visualViewport?.height ?? window.innerHeight
  document.documentElement.style.setProperty('--vh', `${height * 0.01}px`)
}

function initVhFix(): void {
  setVhCustomProperty()
  window.addEventListener('resize', setVhCustomProperty)
  window.addEventListener('orientationchange', setVhCustomProperty)
  window.visualViewport?.addEventListener('resize', setVhCustomProperty)
}

function applySwitchAccount(): void {
  const container = document.querySelector<HTMLElement>('.cmp-header__switch-account')
  const trigger = container?.querySelector<HTMLElement>('.cmp-header__top-item-switch-account-trigger')
  const triggerText = trigger?.querySelector<HTMLElement>('.cmp-header__top-item-title-text')
  const link = container?.querySelector<HTMLAnchorElement>('.cmp-header__top-item-switch-account a')
  const linkText = link?.querySelector('.cmp-header__top-item-title-text')?.textContent?.trim()

  if (!container || !trigger || !triggerText || !link) return

  if (!trigger.dataset.copMobileWired) {
    trigger.dataset.copMobileWired = 'true'
    triggerText.dataset.copOriginalText = triggerText.textContent ?? ''
    trigger.addEventListener(
      'click',
      event => {
        if (!isPowerPortalMobile()) return
        event.preventDefault()
        event.stopPropagation()
        window.location.href = link.href
      },
      true,
    )
  }

  if (!isPowerPortalMobile()) {
    triggerText.textContent = triggerText.dataset.copOriginalText ?? triggerText.textContent
    triggerText.classList.remove(SWITCH_ACCOUNT_LABEL_CLASS)
    container.querySelector(`.${ACCOUNT_NAME_CLASS}`)?.remove()
    return
  }

  if (linkText) {
    triggerText.textContent = linkText
    triggerText.classList.add(SWITCH_ACCOUNT_LABEL_CLASS)
  }

  const customerName = sessionStorage.getItem(CUSTOMER_NAME_KEY)
  const existingAccountNameEl = container.querySelector<HTMLElement>(`.${ACCOUNT_NAME_CLASS}`)

  if (!customerName) {
    existingAccountNameEl?.remove()
    return
  }

  let accountNameEl = existingAccountNameEl
  if (!accountNameEl) {
    accountNameEl = document.createElement('div')
    accountNameEl.className = ACCOUNT_NAME_CLASS

    const label = document.createElement('span')
    label.className = 'cop-header-mobile__account-label'
    label.textContent = 'Account Name'

    const value = document.createElement('span')
    value.className = 'cop-header-mobile__account-value'

    accountNameEl.append(label, value)
    trigger.before(accountNameEl)
  }

  const valueEl = accountNameEl.querySelector('.cop-header-mobile__account-value')
  if (valueEl) {
    valueEl.textContent = customerName
  }
}

function buildProfileDrawer(): { drawer: HTMLElement; title: HTMLElement; list: HTMLElement; back: HTMLButtonElement } {
  const drawer = document.createElement('div')
  drawer.className = PROFILE_DRAWER_CLASS

  const back = document.createElement('button')
  back.type = 'button'
  back.className = `${PROFILE_DRAWER_CLASS}-back`
  const backIcon = document.createElement('span')
  backIcon.className = 'icon-keyboard_arrow_left'
  const backText = document.createElement('span')
  backText.textContent = 'Menu'
  back.append(backIcon, backText)

  const title = document.createElement('p')
  title.className = `${PROFILE_DRAWER_CLASS}-title`

  const list = document.createElement('div')
  list.className = `${PROFILE_DRAWER_CLASS}-list`

  drawer.append(back, title, list)
  return { drawer, title, list, back }
}

function refreshProfileDrawerItems(list: HTMLElement): void {
  list.innerHTML = ''

  const adminModeLink = document.querySelector<HTMLAnchorElement>('.cmp-header__top-item-admin-mode a')
  const logoutLink = document.querySelector<HTMLAnchorElement>('.cmp-header__top-item-logout a')

  if (adminModeLink) {
    const clone = adminModeLink.cloneNode(true) as HTMLAnchorElement
    clone.classList.add(`${PROFILE_DRAWER_CLASS}-item`)
    list.append(clone)
  }

  if (logoutLink) {
    const clone = logoutLink.cloneNode(true) as HTMLAnchorElement
    clone.classList.add(`${PROFILE_DRAWER_CLASS}-item`)
    list.append(clone)
  }
}

function enhanceProfileDrawer(): void {
  if (!isPowerPortalMobile()) return

  const profileTitle = document.querySelector<HTMLElement>('.cmp-header__top-item-profile')
  const nav = document.querySelector<HTMLElement>('.cmp-navigation')

  if (!profileTitle || !nav || profileTitle.dataset.copDrawerWired) return
  profileTitle.dataset.copDrawerWired = 'true'

  if (!profileTitle.hasAttribute('tabindex')) profileTitle.setAttribute('tabindex', '0')
  profileTitle.setAttribute('role', 'button')
  profileTitle.setAttribute('aria-haspopup', 'true')
  profileTitle.setAttribute(ARIA_EXPANDED, 'false')

  const { drawer, title, list, back } = buildProfileDrawer()
  nav.after(drawer)

  const usernameEl = profileTitle.querySelector('.cmp-header__top-item-username')
  const updateTitle = () => {
    title.textContent = usernameEl?.textContent?.trim() ?? ''
  }
  updateTitle()
  window.addEventListener('userDataReady', updateTitle)

  const openDrawer = () => {
    updateTitle()
    refreshProfileDrawerItems(list)
    drawer.classList.add(`${PROFILE_DRAWER_CLASS}--open`)
    nav.classList.add(`${PROFILE_DRAWER_CLASS}--nav-hidden`)
    profileTitle.setAttribute(ARIA_EXPANDED, 'true')
    back.focus()
  }

  const closeDrawer = () => {
    drawer.classList.remove(`${PROFILE_DRAWER_CLASS}--open`)
    nav.classList.remove(`${PROFILE_DRAWER_CLASS}--nav-hidden`)
    profileTitle.setAttribute(ARIA_EXPANDED, 'false')
    profileTitle.focus()
  }

  back.addEventListener('click', closeDrawer)

  profileTitle.addEventListener(
    'click',
    event => {
      if (!isPowerPortalMobile()) return
      event.preventDefault()
      event.stopPropagation()
      openDrawer()
    },
    true,
  )

  profileTitle.addEventListener('keydown', event => {
    if (!isPowerPortalMobile()) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    event.stopPropagation()
    openDrawer()
  })
}

function closeProfileDrawerOnDesktop(): void {
  if (isPowerPortalMobile()) return

  document.querySelector(`.${PROFILE_DRAWER_CLASS}`)?.classList.remove(`${PROFILE_DRAWER_CLASS}--open`)
  document.querySelector('.cmp-navigation')?.classList.remove(`${PROFILE_DRAWER_CLASS}--nav-hidden`)
}

function init(): void {
  if (!document.body.classList.contains('cmp-powerportal-main')) return

  initVhFix()
  applySwitchAccount()
  enhanceProfileDrawer()

  window.matchMedia(MOBILE_BREAKPOINT).addEventListener('change', () => {
    applySwitchAccount()
    enhanceProfileDrawer()
    closeProfileDrawerOnDesktop()
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
